import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

/**
 * Trix Toolbar Controller
 * 
 * Customizes the Trix editor toolbar with:
 * - H1, H2, H3 heading buttons
 * - Subscript button
 * - Custom image upload with direct upload to S3 and server-side processing
 */
export default class extends Controller {
  connect() {
    // In-memory map of URL → citation object ({ id, url, user_citation, ... }).
    // Trix strips data-* attrs from its rendered DOM and continuously overwrites
    // the hidden input from its internal model, so we can't rely on the DOM.
    this._citations = new Map()

    // Register heading attributes immediately (before Trix initializes)
    this.registerHeadingAttributes()

    // Find the trix-editor element within this controller's scope
    const editor = this.element.querySelector('trix-editor')
    if (!editor) return

    this.editor = editor
    this._citableContext = null

    // Wait for Trix to fully initialize
    editor.addEventListener('trix-initialize', this.handleInitialize.bind(this), { once: true })

    // Intercept default file attachment to prevent duplicates
    editor.addEventListener('trix-file-accept', this.handleFileAccept.bind(this))
  }

  handleInitialize(event) {
    this.customizeToolbar(event)

    // Load citation URLs from the server for this citable model
    this.loadCitationsFromServer()

    // Re-inject data-citation-id attrs after Trix changes (debounced)
    this._boundInject = this.debounce(() => this.injectCitationIdsIntoHTML(), 300)
    this.editor.addEventListener('trix-change', this._boundInject)

    // Swap link/citation button highlights based on cursor position
    this.editor.addEventListener('trix-selection-change', () => this.updateCitationButtonState())
  }

  disconnect() {
    if (this.editor) {
      if (this._boundInject) this.editor.removeEventListener('trix-change', this._boundInject)
      this.hideCitationDialog()
    }
  }

  handleFileAccept(event) {
    // Prevent default Trix attachment behavior for images
    // We'll handle image uploads through our custom button
    const file = event.file
    if (file && file.type.startsWith('image/')) {
      event.preventDefault()
      // Trigger our custom upload flow instead
      this.uploadAndInsertImage(file)
    }
  }

  customizeToolbar(event) {
    const editor = event.target
    const toolbar = editor.toolbarElement
    
    if (!toolbar) return

    // Find the heading-1 button group
    const headingButton = toolbar.querySelector('[data-trix-attribute="heading1"]')
    if (headingButton) {
    const buttonGroup = headingButton.closest('.trix-button-group')
      if (buttonGroup) {
    // Create H2 button
    const h2Button = this.createHeadingButton('heading2', 'H2', 'heading-2')
    
    // Create H3 button
    const h3Button = this.createHeadingButton('heading3', 'H3', 'heading-3')

    // Insert H2 and H3 after H1
    headingButton.parentNode.insertBefore(h2Button, headingButton.nextSibling)
    h2Button.parentNode.insertBefore(h3Button, h2Button.nextSibling)
      }
    }
    
    // Add subscript button after strikethrough
    const strikeButton = toolbar.querySelector('[data-trix-attribute="strike"]')
    if (strikeButton) {
      const subButton = this.createInlineButton('sub', 'Subscript', 'sub')
      strikeButton.parentNode.insertBefore(subButton, strikeButton.nextSibling)
    }
    
    // Replace the default attach button with our custom image upload button
    this.replaceAttachButton(toolbar)

    // Add citation button
    this.addCiteButton(toolbar)

    console.log('Trix toolbar customized: H1, H2, H3, subscript, cite buttons added')
  }

  replaceAttachButton(toolbar) {
    // Find the attach files button
    const attachButton = toolbar.querySelector('[data-trix-action="attachFiles"]')
    if (!attachButton) return
    
    // Create our custom image button
    const imageButton = document.createElement('button')
    imageButton.type = 'button'
    imageButton.className = 'trix-button trix-button--icon trix-button--icon-image-upload'
    imageButton.setAttribute('title', 'Insert Image')
    imageButton.setAttribute('tabindex', '-1')
    imageButton.innerHTML = '' // Icon via CSS
    
    // Add click handler
    imageButton.addEventListener('click', (e) => {
      e.preventDefault()
      this.openImagePicker()
    })
    
    // Replace the attach button with our image button
    attachButton.parentNode.replaceChild(imageButton, attachButton)
  }

  openImagePicker() {
    // Create a hidden file input
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.style.display = 'none'
    
    input.addEventListener('change', (e) => {
      const file = e.target.files[0]
      if (file) {
        this.uploadAndInsertImage(file)
      }
      input.remove()
    })
    
    document.body.appendChild(input)
    input.click()
  }

  async uploadAndInsertImage(file) {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select a valid image file (JPG, PNG, GIF)')
      return
    }
    
    // Validate file size (max 25MB)
    const maxSize = 25 * 1024 * 1024
    if (file.size > maxSize) {
      alert('Image is too large. Maximum size is 25MB.')
      return
    }
    
    // Show upload modal
    this.showUploadModal(file.name)
    
    try {
      // Step 1: Upload to S3 via Direct Upload
      this.updateModalStatus('Uploading to cloud...', 0)
      const blob = await this.directUpload(file)
      
      // Step 2: Process the image on the server
      this.updateModalStatus('Optimizing image...', 80)
      const processedImage = await this.processImage(blob.signed_id)
      
      // Step 3: Insert the image into the editor
      this.updateModalStatus('Inserting image...', 95)
      this.insertImageIntoEditor(processedImage.url)
      
      // Done!
      this.updateModalStatus('Complete!', 100)
      setTimeout(() => this.hideUploadModal(), 500)
      
    } catch (error) {
      console.error('[TrixImage] Upload failed:', error)
      this.showModalError(error.message || 'Upload failed. Please try again.')
    }
  }

  directUpload(file) {
    return new Promise((resolve, reject) => {
      const url = document.querySelector('meta[name="direct-upload-url"]')?.content || '/rails/active_storage/direct_uploads'
      
      const upload = new DirectUpload(file, url, {
        directUploadWillStoreFileWithXHR: (request) => {
          request.upload.addEventListener('progress', (event) => {
            const progress = Math.round((event.loaded / event.total) * 70) // 0-70% for upload
            this.updateModalStatus('Uploading to cloud...', progress)
          })
        }
      })

      upload.create((error, blob) => {
        if (error) {
          reject(new Error('Upload to cloud failed'))
        } else {
          resolve(blob)
        }
      })
    })
  }

  async processImage(signedId) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    
    const response = await fetch('/api/content_images/process', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify({ signed_id: signedId })
    })
    
    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      throw new Error(data.error || 'Image processing failed')
    }
    
    return response.json()
  }

  insertImageIntoEditor(imageUrl) {
    if (!this.editor) return
    
    // Create an HTML string for the image
    const imgHtml = `<img src="${imageUrl}" class="w-100" style="max-width: 1000px;">`
    
    // Get the Trix editor instance
    const trixEditor = this.editor.editor
    
    // Insert the HTML at the current cursor position
    // Trix uses attachments for inline content, but we want a simple img tag
    // We'll use insertHTML which works for basic HTML
    trixEditor.insertHTML(imgHtml)
    
    // Add a line break after the image for better editing experience
    trixEditor.insertLineBreak()
  }

  // Modal UI Methods
  showUploadModal(filename) {
    // Remove any existing modal
    this.hideUploadModal()
    
    const modal = document.createElement('div')
    modal.id = 'trix-image-upload-modal'
    modal.className = 'trix-upload-modal'
    modal.innerHTML = `
      <div class="trix-upload-modal-backdrop"></div>
      <div class="trix-upload-modal-content">
        <div class="trix-upload-modal-icon">
          <i class="bi bi-cloud-arrow-up"></i>
        </div>
        <div class="trix-upload-modal-filename">${this.truncateFilename(filename)}</div>
        <div class="trix-upload-modal-progress">
          <div class="trix-upload-modal-progress-bar" style="width: 0%"></div>
        </div>
        <div class="trix-upload-modal-status">Preparing...</div>
      </div>
    `
    
    document.body.appendChild(modal)
    
    // Trigger animation
    requestAnimationFrame(() => {
      modal.classList.add('active')
    })
  }

  updateModalStatus(status, progress) {
    const modal = document.getElementById('trix-image-upload-modal')
    if (!modal) return
    
    const statusEl = modal.querySelector('.trix-upload-modal-status')
    const progressBar = modal.querySelector('.trix-upload-modal-progress-bar')
    
    if (statusEl) statusEl.textContent = status
    if (progressBar) progressBar.style.width = `${progress}%`
  }

  showModalError(message) {
    const modal = document.getElementById('trix-image-upload-modal')
    if (!modal) return
    
    const content = modal.querySelector('.trix-upload-modal-content')
    if (content) {
      content.innerHTML = `
        <div class="trix-upload-modal-icon text-danger">
          <i class="bi bi-exclamation-triangle"></i>
        </div>
        <div class="trix-upload-modal-status text-danger">${this.escapeHtml(message)}</div>
        <button type="button" class="btn btn-outline-secondary btn-sm mt-3" onclick="document.getElementById('trix-image-upload-modal').remove()">
          Close
        </button>
      `
    }
  }

  hideUploadModal() {
    const modal = document.getElementById('trix-image-upload-modal')
    if (modal) {
      modal.classList.remove('active')
      setTimeout(() => modal.remove(), 300)
    }
  }

  truncateFilename(filename, maxLength = 30) {
    if (filename.length <= maxLength) return filename
    const ext = filename.split('.').pop()
    const name = filename.slice(0, maxLength - ext.length - 4)
    return `${name}...${ext}`
  }

  createHeadingButton(attribute, label, iconClass) {
    const button = document.createElement('button')
    button.type = 'button'
    button.className = `trix-button trix-button--icon trix-button--icon-${iconClass}`
    button.setAttribute('data-trix-attribute', attribute)
    button.setAttribute('title', label)
    button.setAttribute('tabindex', '-1')
    // Icon is displayed via CSS ::before pseudo-element (SVG background-image)
    
    return button
  }

  createInlineButton(attribute, label, iconClass) {
    const button = document.createElement('button')
    button.type = 'button'
    button.className = `trix-button trix-button--icon trix-button--icon-${iconClass}`
    button.setAttribute('data-trix-attribute', attribute)
    button.setAttribute('title', label)
    button.setAttribute('tabindex', '-1')
    
    return button
  }

  // ── Citation Methods ──────────────────────────────────────────────

  addCiteButton(toolbar) {
    const linkButton = toolbar.querySelector('[data-trix-attribute="href"]')
    if (!linkButton) return

    const citeButton = document.createElement('button')
    citeButton.type = 'button'
    citeButton.className = 'trix-button trix-button--icon trix-button--icon-cite'
    citeButton.setAttribute('title', 'Add Citation')
    citeButton.setAttribute('tabindex', '-1')

    citeButton.addEventListener('mousedown', (e) => {
      e.preventDefault()
      this.showCitationDialog()
    })

    linkButton.parentNode.insertBefore(citeButton, linkButton.nextSibling)
  }

  updateCitationButtonState() {
    const toolbar = this.editor.toolbarElement
    if (!toolbar) return

    const linkButton = toolbar.querySelector('[data-trix-attribute="href"]')
    const citeButton = toolbar.querySelector('.trix-button--icon-cite')
    if (!linkButton || !citeButton) return

    const currentHref = this.editor.editor.composition.getCurrentAttributes().href
    const isCitation = currentHref && this._citations.has(currentHref)

    if (isCitation) {
      linkButton.classList.remove('trix-active')
      citeButton.classList.add('trix-active')
    } else {
      citeButton.classList.remove('trix-active')
      if (currentHref) {
        linkButton.classList.add('trix-active')
      }
    }
  }

  getCitableContext() {
    if (this._citableContext) return this._citableContext

    const inputId = this.editor.getAttribute('input')
    const hiddenInput = inputId ? document.getElementById(inputId) : null
    if (!hiddenInput) return null

    const nameMatch = hiddenInput.name.match(/^(\w+)\[(\w+)\]$/)
    if (!nameMatch) return null

    const modelName = nameMatch[1]
    const fieldName = nameMatch[2]
    const citableType = modelName.charAt(0).toUpperCase() + modelName.slice(1)

    const form = this.element.closest('form')
    if (!form) return null

    const formIdMatch = form.id.match(/edit_\w+_(\d+)/)
    let citableId = formIdMatch ? formIdMatch[1] : null

    if (!citableId) {
      const idInput = form.querySelector(`input[name="${modelName}[id]"]`)
      if (idInput) citableId = idInput.value
    }
    if (!citableId) {
      const actionMatch = form.action.match(/\/(\d+)(?:\/|\?|$)/)
      if (actionMatch) citableId = actionMatch[1]
    }
    if (!citableId) return null

    this._citableContext = { citableType, citableId, fieldName }
    return this._citableContext
  }

  async showCitationDialog() {
    const trixEditor = this.editor.editor

    // Expand selection around link (like Trix's native link dialog behavior)
    const currentHref = trixEditor.composition.getCurrentAttributes().href
    if (currentHref) {
      trixEditor.composition.expandSelectionAroundCommonAttribute('href')
    }
    this._savedRange = trixEditor.getSelectedRange()

    // If we have a link but citations haven't been loaded yet, await the load.
    if (currentHref && !this._citationsLoaded) {
      await this.loadCitationsFromServer()
    }

    const existingCitation = currentHref ? (this._citations.get(currentHref) || null) : null
    var editingCitationId = existingCitation?.id || null
    var editingOriginalUrl = currentHref || null
    const editingUrl = currentHref || ''
    const editingUserCitation = existingCitation?.user_citation || ''

    // Remove existing dialog if open
    this.hideCitationDialog()

    const dialog = document.createElement('div')
    dialog.className = 'trix-dialog trix-citation-dialog'
    dialog.innerHTML = `
      <div class="trix-citation-dialog-fields">
        <input type="url" class="trix-citation-url trix-input trix-input--dialog"
               placeholder="https://..." value="${this.escapeAttr(editingUrl)}">
        <input type="text" class="trix-citation-apa trix-input trix-input--dialog"
               placeholder="APA citation (optional)" value="${this.escapeAttr(editingUserCitation)}">
        <div class="trix-button-group">
          <input type="button" class="trix-button trix-button--dialog trix-citation-apply"
                 value="${editingCitationId ? 'Update' : 'Cite'}">
          ${editingCitationId ? '<input type="button" class="trix-button trix-button--dialog trix-citation-remove" value="Remove">' : ''}
          <input type="button" class="trix-button trix-button--dialog trix-citation-cancel" value="Cancel">
        </div>
      </div>
      <div class="trix-citation-error"></div>
    `

    const dialogsContainer = this.editor.toolbarElement.querySelector('.trix-dialogs')
    dialogsContainer.appendChild(dialog)

    dialog.querySelector('.trix-citation-url').focus()

    // Cancel
    dialog.querySelector('.trix-citation-cancel').addEventListener('click', () => {
      this.hideCitationDialog()
    })

    // Dismiss on click outside the dialog
    this._dismissCitationDialog = (e) => {
      if (!dialog.contains(e.target) && !e.target.closest('.trix-button--icon-cite')) {
        this.hideCitationDialog()
      }
    }
    setTimeout(() => document.addEventListener('mousedown', this._dismissCitationDialog), 0)

    // Apply
    dialog.querySelector('.trix-citation-apply').addEventListener('click', () => {
      const url = dialog.querySelector('.trix-citation-url').value.trim()
      const apa = dialog.querySelector('.trix-citation-apa').value.trim()

      if (!url) { this.showCitationError('URL is required'); return }
      if (!url.match(/^https?:\/\//i)) { this.showCitationError('Must start with http:// or https://'); return }

      if (editingCitationId) {
        this.updateCitation(editingCitationId, editingOriginalUrl, url, apa)
      } else {
        this.submitCitation(url, apa)
      }
    })

    // Remove
    const removeBtn = dialog.querySelector('.trix-citation-remove')
    if (removeBtn) {
      removeBtn.addEventListener('click', () => {
        this.removeCitation(editingCitationId, editingOriginalUrl)
      })
    }

    // Keyboard
    dialog.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.hideCitationDialog()
      if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
        e.preventDefault()
        dialog.querySelector('.trix-citation-apply').click()
      }
    })
  }

  hideCitationDialog() {
    if (this._dismissCitationDialog) {
      document.removeEventListener('mousedown', this._dismissCitationDialog)
      this._dismissCitationDialog = null
    }
    const existing = this.element.querySelector('.trix-citation-dialog')
    if (existing) existing.remove()
  }

  showCitationError(message) {
    const errorEl = this.element.querySelector('.trix-citation-error')
    if (errorEl) errorEl.textContent = message
  }

  async submitCitation(url, userCitation) {
    const ctx = this.getCitableContext()
    if (!ctx) { this.showCitationError('Could not determine article context'); return }

    const applyBtn = this.element.querySelector('.trix-citation-apply')
    if (applyBtn) { applyBtn.disabled = true; applyBtn.value = 'Adding...' }

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const response = await fetch('/contributors/citations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({
          citable_type: ctx.citableType, citable_id: ctx.citableId,
          field_name: ctx.fieldName, url, user_citation: userCitation || null
        })
      })

      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        throw new Error(data.error || 'Failed to create citation')
      }

      const citation = await response.json()
      this.applyCitationLink(citation)
      if (citation.reused) {
        console.info('[TrixCitation] Reused existing citation for this URL:', citation.url)
      }
      this.hideCitationDialog()
    } catch (error) {
      console.error('[TrixCitation] Create failed:', error)
      this.showCitationError(error.message || 'Failed to add citation')
      if (applyBtn) { applyBtn.disabled = false; applyBtn.value = 'Cite' }
    }
  }

  async updateCitation(citationId, originalUrl, url, userCitation) {
    const applyBtn = this.element.querySelector('.trix-citation-apply')
    if (applyBtn) { applyBtn.disabled = true; applyBtn.value = 'Updating...' }

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const response = await fetch(`/contributors/citations/${citationId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({ url, user_citation: userCitation || null })
      })

      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        throw new Error(data.error || 'Failed to update citation')
      }


      // Update the href on the existing link via Trix's native mechanism
      const trixEditor = this.editor.editor
      if (this._savedRange) trixEditor.setSelectedRange(this._savedRange)
      trixEditor.activateAttribute('href', url)

      // Track the updated citation
      if (originalUrl && originalUrl !== url) {
        this._citations.delete(originalUrl)
      }
      this.trackCitation({ id: citationId, url, user_citation: userCitation })
      this.hideCitationDialog()
    } catch (error) {
      console.error('[TrixCitation] Update failed:', error)
      this.showCitationError(error.message || 'Failed to update citation')
      if (applyBtn) { applyBtn.disabled = false; applyBtn.value = 'Update' }
    }
  }

  async removeCitation(citationId, url) {
    const removeBtn = this.element.querySelector('.trix-citation-remove')
    if (removeBtn) { removeBtn.disabled = true; removeBtn.value = 'Removing...' }

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const response = await fetch(`/contributors/citations/${citationId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken }
      })

      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        throw new Error(data.error || 'Failed to remove citation')
      }

      if (url) this._citations.delete(url)

      // Remove the link via Trix's native mechanism (keeps text)
      const trixEditor = this.editor.editor
      if (this._savedRange) trixEditor.setSelectedRange(this._savedRange)
      trixEditor.deactivateAttribute('href')
      this.hideCitationDialog()
    } catch (error) {
      console.error('[TrixCitation] Remove failed:', error)
      this.showCitationError(error.message || 'Failed to remove citation')
      if (removeBtn) { removeBtn.disabled = false; removeBtn.value = 'Remove' }
    }
  }

  applyCitationLink(citation) {
    const trixEditor = this.editor.editor

    // Restore saved selection
    if (this._savedRange) trixEditor.setSelectedRange(this._savedRange)

    const hasSelection = this._savedRange && this._savedRange[0] !== this._savedRange[1]

    if (hasSelection) {
      trixEditor.activateAttribute('href', citation.url)
    } else {
      trixEditor.insertString(citation.url)
      const end = trixEditor.getSelectedRange()[1]
      const start = end - citation.url.length
      trixEditor.setSelectedRange([start, end])
      trixEditor.activateAttribute('href', citation.url)
    }

    this.trackCitation(citation)
  }

  trackCitation(citation) {
    this._citations.set(citation.url, citation)
    this.injectCitationIdsIntoHTML()
  }

  // Fetch citation records from the server for this citable model
  // and populate the in-memory URL → id map.
  async loadCitationsFromServer() {
    const ctx = this.getCitableContext()
    if (!ctx) return

    try {
      const params = new URLSearchParams({
        citable_type: ctx.citableType,
        citable_id: ctx.citableId,
        field_name: ctx.fieldName
      })
      const response = await fetch(`/contributors/citations?${params}`)
      if (!response.ok) return

      const citations = await response.json()
      citations.forEach(c => {
        if (c.url && c.id) this._citations.set(c.url, { ...c, id: String(c.id) })
      })

      this._citationsLoaded = true

      // Inject into hidden input so saves include data-citation-id
      this.injectCitationIdsIntoHTML()
    } catch (e) {
      this._citationsLoaded = true
      console.warn('[TrixCitation] Failed to load citations from server:', e)
    }
  }

  // Re-inject data-citation-id attributes into the hidden input HTML.
  // Trix overwrites the hidden input from its internal model on every
  // change, stripping data-* attrs. This re-applies them from the map.
  injectCitationIdsIntoHTML() {
    if (this._citations.size === 0) return

    const inputId = this.editor.getAttribute('input')
    const hiddenInput = inputId ? document.getElementById(inputId) : null
    if (!hiddenInput?.value) return

    const doc = new DOMParser().parseFromString(hiddenInput.value, 'text/html')
    let modified = false

    doc.querySelectorAll('a[href]').forEach(link => {
      const url = link.getAttribute('href')
      const citation = this._citations.get(url)
      if (citation && !link.hasAttribute('data-citation-id')) {
        link.setAttribute('data-citation-id', citation.id)
        modified = true
      }
    })

    if (modified) {
      hiddenInput.value = doc.body.innerHTML
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  escapeAttr(str) {
    return str.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  }

  debounce(fn, ms) {
    let timer
    return (...args) => {
      clearTimeout(timer)
      timer = setTimeout(() => fn.apply(this, args), ms)
    }
  }

  // ── Heading & Attribute Registration ───────────────────────────────

  registerHeadingAttributes() {
    // Only register if not already registered
    if (window.Trix && !window.headingAttributesRegistered) {
      const { config } = window.Trix

      // Register H2
      config.blockAttributes.heading2 = {
        tagName: 'h2',
        terminal: true,
        breakOnReturn: true,
        group: false
      }

      // Register H3
      config.blockAttributes.heading3 = {
        tagName: 'h3',
        terminal: true,
        breakOnReturn: true,
        group: false
      }

      // Register subscript (inline attribute, not block)
      config.textAttributes.sub = {
        tagName: 'sub',
        inheritable: true
      }

      window.headingAttributesRegistered = true
    }
  }
}
